import React, { FC, useState, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";

import ArrowIcon from "../../../../assets/icons/ArrowIcon";
import { findBrandByDomain } from "../../../../configs/configLoader";
import { resultScreenUI } from "../../../../configs";
import {
  capitalizeFirstLetter,
  getCurrencySymbol,
  useIsMobile,
} from "../../../../utils";
import ArrowIconRight from "../../../../assets/icons/ArrowIconRight";
import { SimilarProductType } from "../../../../types/result";

import "./index.css";

interface IPropsUnavailableGallery {
  similarProducts: SimilarProductType[] | null;
}

const UnavailableGallery: FC<IPropsUnavailableGallery> = ({
  similarProducts,
}) => {
  const isMobile = useIsMobile();

  const isLargeHeightRelative = useMediaQuery({
    maxHeight: !isMobile ? 830 : 778,
    minWidth: 767,
  });

  const brandDefined = findBrandByDomain();

  const isGivenchy = brandDefined?.name === "Givenchy";
  const isDevernois = brandDefined?.name === "Devernois";

  const isGivenchyMobile = isGivenchy && isMobile;
  const isGivenchyDesktop = isGivenchy && !isMobile;

  const isDesktopGerardDarel =
    brandDefined?.name === "Gerard Darel" && !isMobile;

  const isIOSMobile =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

  const { t } = useTranslation("components/results/result");

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftX, setScrollLeftX] = useState(0);
  const [draggingThresholdExceeded, setDraggingThresholdExceeded] =
    useState(false);

  const [isLeftArrowVisible, setIsLeftArrowVisible] = useState(false);
  const [isRightArrowVisible, setIsRightArrowVisible] = useState(true);

  // const [ratio, setRatio] = useState<number | null>(null);

  const [ratios, setRatios] = useState<Record<number, number>>({});

  const scrollByItem = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const itemWidth =
        scrollRef.current.querySelector(".info")?.clientWidth || 160;
      const gap = 10;
      const totalScrollAmount = itemWidth + gap;
      const newScrollPosition =
        scrollRef.current.scrollLeft +
        (direction === "right" ? totalScrollAmount : -totalScrollAmount);

      scrollRef.current.scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      });

      setIsLeftArrowVisible(newScrollPosition > 0);
      setIsRightArrowVisible(
        newScrollPosition + scrollRef.current.clientWidth <
          scrollRef.current.scrollWidth,
      );
    }
  };

  const handleClick = (item: any, index: number) => {
    // const id = extractIdFromUrl(item.image_url);

    if (!draggingThresholdExceeded) {
      if (item.product_url) {
        window.open(item.product_url, "_self");
      }
    }
    setDraggingThresholdExceeded(false); // Reset after click
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scrollRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - scrollRef.current.offsetLeft);
      setScrollLeftX(scrollRef.current.scrollLeft);
      setDraggingThresholdExceeded(false); // Reset threshold on new drag
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;

    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeftX - walk;

    setIsLeftArrowVisible(scrollRef.current.scrollLeft > 0);
    setIsRightArrowVisible(
      scrollRef.current.scrollLeft + scrollRef.current.clientWidth <
        scrollRef.current.scrollWidth,
    );
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (scrollRef.current) {
      setIsDragging(true);
      setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
      setScrollLeftX(scrollRef.current.scrollLeft);
      setDraggingThresholdExceeded(false);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollRef.current) return;

    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;

    if (Math.abs(walk) > 5) {
      setDraggingThresholdExceeded(true);
    }

    scrollRef.current.scrollLeft = scrollLeftX - walk;

    setIsLeftArrowVisible(scrollRef.current.scrollLeft > 0);
    setIsRightArrowVisible(
      scrollRef.current.scrollLeft + scrollRef.current.clientWidth <
        scrollRef.current.scrollWidth,
    );
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const productTitle =
    (resultScreenUI.similarProducts as any)?.productTitle || {};

  const productTitleStyles: React.CSSProperties = {
    color: productTitle.fontColor as React.CSSProperties["color"],
    fontSize: productTitle.fontSize as React.CSSProperties["fontSize"],
    fontWeight:
      (productTitle.fontWeight as React.CSSProperties["fontWeight"]) ||
      "normal",
    letterSpacing: (resultScreenUI.Description as any)?.letterSpacing,
    lineHeight: "20px",
  };

  const scrollContainerStyles: React.CSSProperties = {
    overflowX: "auto" as React.CSSProperties["overflowX"],
    display: "inline-flex",
    marginBottom: isMobile ? "10px" : 0,
    cursor: isDragging ? "grabbing" : "grab",
    width: isGivenchyDesktop
      ? "100%"
      : isLargeHeightRelative
        ? "auto"
        : "600px",
  };

  const handleTitleGalleryMargin = () => {
    if (isGivenchy) return "0px";
    if (isDesktopGerardDarel) return "0";
    if (isMobile) return "5px 0 5px 0";
    return "20px 0 5px 0";
  };

  const handleImageWidth = () => {
    if (isGivenchyDesktop) return "560px";
    if (isGivenchyMobile) return "356px";
    if (isIOSMobile) return "180px";
    if (isMobile) return "250px";
    return "273px";
  };

  const toSentenceCase = (str: string) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  const titleGalleryStyles: React.CSSProperties = {
    color: resultScreenUI.similarProducts.title
      .fontColor as React.CSSProperties["color"],
    fontSize: resultScreenUI.similarProducts.title
      .fontSize as React.CSSProperties["fontSize"],
    fontWeight: resultScreenUI.similarProducts.title
      .fontWeight as React.CSSProperties["fontWeight"],
    textAlign: resultScreenUI.similarProducts.title
      .textAlign as React.CSSProperties["textAlign"],
    margin: handleTitleGalleryMargin(),
    letterSpacing: (resultScreenUI.Description as any)?.letterSpacing,
    lineHeight: (resultScreenUI.similarProducts as any)?.lineHeight,
  };

  const normalizeProductTitle = (title: string): string => {
    if (!title) return "";

    const regex = /^([^\d/]+).*/;
    const match = title.match(regex);
    if (match && match[1]) {
      return match[1].trim();
    }

    return title;
  };

  return (
    <div
      className="gallery-container"
      style={{
        gap: isGivenchy ? "20px" : "10px",
        width: isGivenchyDesktop ? "100%" : "",
      }}
    >
      <p className="title" style={titleGalleryStyles}>
        {t("unavailable.title")}
      </p>
      <div style={{ display: "flex", alignItems: "center" }}>
        {isLeftArrowVisible ? (
          <div
            onClick={() => scrollByItem("left")}
            className="left-arrow"
            style={{
              width: isGivenchy ? "40px" : "24px",
              height: isGivenchy ? "40px" : "24px",
              marginBottom: isGivenchy ? "80px" : "40px",
              backgroundColor: isGivenchy ? "transparent" : "#fff",
            }}
          >
            {isGivenchy ? (
              <ArrowIconRight
                style={{
                  transform: "rotate(180deg)",
                  fontSize: isGivenchy ? 40 : 24,
                }}
              />
            ) : (
              <ArrowIcon />
            )}
          </div>
        ) : null}
        <div
          className="scroll-container"
          ref={scrollRef}
          style={scrollContainerStyles}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {similarProducts?.map((item, index) => {
            const title = isGivenchy
              ? capitalizeFirstLetter(item.title)
              : item.title;

            return (
              <div
                key={index}
                className="info"
                onClick={() => handleClick(item, index)}
                style={{
                  flex: "0 0 auto",
                }}
              >
                <div
                  className="img-container"
                  style={{
                    backgroundColor: "#fff",
                    overflow: "hidden",
                    height: handleImageWidth(),
                    aspectRatio: ratios[index] || "3 / 4",
                  }}
                >
                  <img
                    src={item.image_url}
                    alt=""
                    loading="lazy"
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      if (img.naturalWidth && img.naturalHeight) {
                        setRatios((prev) => ({
                          ...prev,
                          [index]: img.naturalWidth / img.naturalHeight,
                        }));
                      }
                    }}
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      objectFit: "cover",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: isGivenchy ? "5px" : "15px",
                  }}
                >
                  <p
                    className="info-text"
                    style={{
                      ...(productTitle ? productTitleStyles : undefined),
                      textAlign: isGivenchy ? "center" : "left",
                      maxWidth: handleImageWidth(),
                    }}
                  >
                    {isDevernois
                      ? toSentenceCase(normalizeProductTitle(title))
                      : title}
                  </p>
                  {item?.price ? (
                    <p
                      className="info-text"
                      style={{
                        ...(productTitle ? productTitleStyles : undefined),
                        textAlign: isGivenchy ? "center" : "left",
                        maxWidth: handleImageWidth(),
                      }}
                    >
                      {isGivenchy ? (
                        <>
                          {getCurrencySymbol(item?.price?.currency)}
                          {Number(item?.price?.current)}
                        </>
                      ) : (
                        <>
                          {Number(item?.price?.current)}
                          {getCurrencySymbol(item?.price?.currency)}
                        </>
                      )}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        {isRightArrowVisible ? (
          <div
            onClick={() => scrollByItem("right")}
            className="right-arrow"
            style={{
              width: isGivenchy ? "40px" : "24px",
              height: isGivenchy ? "40px" : "24px",
              marginBottom: isGivenchy ? "80px" : "40px",
              backgroundColor: isGivenchy ? "transparent" : "#fff",
            }}
          >
            {isGivenchy ? (
              <ArrowIconRight style={{ fontSize: isGivenchy ? 40 : 24 }} />
            ) : (
              <ArrowIcon style={{ transform: "rotate(180deg)" }} />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UnavailableGallery;
